import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { FiPlusCircle, FiX, FiCheck } from 'react-icons/fi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuggestBookButton() {
  const { token, getAuthHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const submit = async () => {
    setError('');
    if (!title.trim() || !author.trim()) { setError('Both title and author required.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/book-suggestions`,
        { title: title.trim(), author: author.trim() },
        { headers: getAuthHeader() }
      );
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setTitle(''); setAuthor(''); setResult(null); setError(''); setOpen(false); };

  if (!token) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-3 py-2 bg-purple-50 hover:bg-purple-100 border border-purple-200 text-purple-700 rounded-lg text-sm font-semibold"
        data-testid="suggest-book-btn"
      >
        <FiPlusCircle className="w-4 h-4" />
        Suggest a book
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="suggest-book-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800">Suggest a book for sourcing</h3>
              <button onClick={reset} className="text-gray-400 hover:text-gray-600"><FiX /></button>
            </div>

            {result ? (
              <div className="p-6 text-center" data-testid="suggest-success">
                <FiCheck className="w-12 h-12 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-gray-800">
                  {result.merged || result.already_voted ? 'Vote recorded!' : 'Thanks for the suggestion!'}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {result.merged && `Someone else suggested this too — now ${result.vote_count} votes.`}
                  {result.already_voted && `You already suggested this — it's at ${result.vote_count} vote${result.vote_count === 1 ? '' : 's'}.`}
                  {!result.merged && !result.already_voted && `We'll review and try to source it. You'll see it tracked in your profile.`}
                </p>
                <button
                  onClick={reset}
                  className="mt-5 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold"
                >Close</button>
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <p className="text-sm text-gray-600">
                  Help us grow the library. We&apos;ll consider every suggestion and you&apos;ll be notified when we source it.
                </p>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Book title</label>
                  <input
                    type="text" value={title} maxLength={300}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. Project Hail Mary"
                    data-testid="suggest-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Author</label>
                  <input
                    type="text" value={author} maxLength={200}
                    onChange={(e) => setAuthor(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="e.g. Andy Weir"
                    data-testid="suggest-author-input"
                  />
                </div>
                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2" data-testid="suggest-error">{error}</p>
                )}
                <div className="flex gap-2 justify-end">
                  <button onClick={reset} className="px-4 py-2 border border-gray-300 rounded text-sm">Cancel</button>
                  <button
                    onClick={submit} disabled={submitting}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded text-sm font-semibold disabled:opacity-50"
                    data-testid="suggest-submit-btn"
                  >{submitting ? 'Submitting…' : 'Submit suggestion'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
